import React from 'react';
import { useTranslation } from 'react-i18next';

type ProviderRequestAdminDetailsInput = {
  formPayload?: Record<string, unknown> | null;
  deliveryStatus?: 'sending' | 'sent' | 'failed';
  emailSentAt?: string | null;
  emailFailedAt?: string | null;
  emailFailureReason?: string | null;
  emailProviderMessageId?: string | null;
};

function formatDateTime(value?: string | null): string {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('sv-SE', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function hasPayload(payload?: Record<string, unknown> | null): boolean {
  return Boolean(payload && Object.keys(payload).length > 0);
}

export function ProviderRequestMailStatus({
  request
}: {
  request: ProviderRequestAdminDetailsInput;
}) {
  const { t } = useTranslation();

  if (request.deliveryStatus === 'failed' || request.emailFailedAt) {
    return (
      <div>
        <span className="badge bg-danger">
          <i className="feather-alert-triangle me-1"></i>
          {t('adminProviderRequestDetails.email.failed')}
        </span>
        <div className="text-muted small mt-1">
          {formatDateTime(request.emailFailedAt)}
        </div>
        {request.emailFailureReason && (
          <div className="text-muted small text-break">
            {request.emailFailureReason}
          </div>
        )}
      </div>
    );
  }

  if (request.deliveryStatus === 'sending') {
    return (
      <div>
        <span className="badge bg-warning text-dark">
          <i className="feather-clock me-1"></i>
          {t('adminProviderRequestDetails.email.processing')}
        </span>
        <div className="text-muted small mt-1">
          {t('adminProviderRequestDetails.email.processingInfo')}
        </div>
      </div>
    );
  }

  if (request.emailSentAt) {
    return (
      <div>
        <span className="badge bg-success">
          <i className="feather-mail me-1"></i>
          {t('adminProviderRequestDetails.email.sent')}
        </span>
        <div className="text-muted small mt-1">
          {formatDateTime(request.emailSentAt)}
        </div>
        {request.emailProviderMessageId && (
          <div className="text-muted small text-break">
            {request.emailProviderMessageId}
          </div>
        )}
      </div>
    );
  }

  return (
    <span className="badge bg-secondary">
      <i className="feather-clock me-1"></i>
      {t('adminProviderRequestDetails.email.notAttempted')}
    </span>
  );
}

export function ProviderRequestRawPayload({
  request,
  expanded,
  onToggle
}: {
  request: ProviderRequestAdminDetailsInput;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const payload = request.formPayload ?? {};
  const payloadText = hasPayload(payload)
    ? JSON.stringify(payload, null, 2)
    : t('adminProviderRequestDetails.rawPayload.empty');

  return (
    <div className="mt-2">
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <i
          className={`feather-${expanded ? 'chevron-up' : 'chevron-down'} me-1`}
        ></i>
        {t('adminProviderRequestDetails.rawPayload.title')}
      </button>
      {expanded && (
        <div className="border rounded mt-2 p-3 bg-light">
          <div className="fw-semibold mb-2">
            {t('adminProviderRequestDetails.rawPayload.title')}
          </div>
          <pre
            className="mb-0 small text-break"
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {payloadText}
          </pre>
        </div>
      )}
    </div>
  );
}
